import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { compraItems, type CompraItem } from '@/components/TipoFacturaSelector';

export interface CodigoContable extends CompraItem {
  id?: string;
  activo?: boolean;
}

/**
 * Fuente única de los códigos contables para toda la app.
 *
 * - `todos`: para MOSTRAR el nombre de un código ya asignado (incluye inactivos:
 *   una factura vieja puede apuntar a un código que hoy se desactivó, y su nombre
 *   tiene que seguir resolviéndose).
 * - `activos`: para ELEGIR uno nuevo (dropdowns). Solo los activos.
 *
 * Si la API todavía no respondió o falló, cae al array hardcodeado `compraItems`
 * (el seed), así el dropdown nunca queda vacío ni la app se rompe por esta feature.
 */
export function useCodigosContables(): { todos: CodigoContable[]; activos: CodigoContable[]; isLoading: boolean } {
  const { data, isLoading } = useQuery({
    queryKey: ['codigos-contables'],
    queryFn: () => api.getCodigosContables(),
    staleTime: 5 * 60 * 1000,
  });

  if (!data?.codigos || data.codigos.length === 0) {
    // Fallback: el seed. Todos se consideran activos.
    return { todos: compraItems, activos: compraItems, isLoading };
  }

  const todos = data.codigos.map((c) => ({ id: c.id, codigo: c.codigo, nombre: c.nombre, activo: c.activo }));
  const activos = todos.filter((c) => c.activo !== false);
  return { todos, activos, isLoading };
}
